import React, { createContext, useEffect, useMemo, useState } from 'react';
import InnerPrinterReceipt from '../components/print/InnerPrinterReceipt';
import InnerPrinterOrderHistoryReceipt from '../components/print/InnerPrinterOrderHistoryReceipt';
import useNativeApp from '../hooks/useNativeApp';
import usePrinters from '../hooks/usePrinters';
import useServiceProviderLogo from '../hooks/useServiceProviderLogo';
import ConnectInnerPrinterHandler from '../services/resolvers/rnwebview/handlers/ConnectInnerPrinterHandler';
import WebViewCommunicatorResource from '../services/resources/WebViewCommunicatorResource';
import { Destination } from '../services/exports/Constants';

export const ReceiptType = {
  Order: 'order',
  OrderHistory: 'order_history',
};

export const InnerPrinterReceiptElementID = 'inner-printer-receipt';

export const PrintContext = createContext(null);

export default function PrintProvider({ children }) {
  // Holds the app-wide subscription to the printers query so the list is already warm by
  // the time the first order needs auto-printing. The hook gates itself on login.
  const { externalPrinterEnabled } = usePrinters();

  // Same rationale as usePrinters() above: warm the logo at boot, so the receipt — which mounts
  // only ~50ms before the screenshot — never waits on the fetch.
  useServiceProviderLogo();

  const { isNativeShell, supportsPrinterBridge } = useNativeApp();

  const [data, setData] = useState(null);

  useEffect(() => {
    if (supportsPrinterBridge) {
      WebViewCommunicatorResource.connectPrinter({ destination: Destination.Sunmi });

      return;
    }

    // Older shells ignore CONNECT_PRINTER, and `print` is not persisted — without this,
    // innerPrinterConnected sits at false all session and receipts stop reaching the built-in
    // printer. Those builds always reported `true`. Shell-guarded so a plain browser keeps its
    // false flag, and with it usePrinter's server-side fallback.
    if (isNativeShell) {
      new ConnectInnerPrinterHandler({ innerPrinterConnected: true }).notify();
    }
  }, [isNativeShell, supportsPrinterBridge]);

  // Kept apart from the built-in probe above: the company flag behind `externalPrinterEnabled`
  // only lands once the profile resolves, so this effect has to re-run on it — and the Sunmi
  // probe must not be re-sent when it does.
  useEffect(() => {
    if (!externalPrinterEnabled) {
      return;
    }

    WebViewCommunicatorResource.connectPrinter({ destination: Destination.External });
  }, [externalPrinterEnabled]);

  const context = useMemo(
    () => ({
      data,
      setData,
    }),
    [data],
  );

  const renderReceipt = useMemo(() => {
    switch (data?.type) {
      case ReceiptType.Order:
        return <InnerPrinterReceipt payload={data?.payload} />;
      case ReceiptType.OrderHistory:
        return <InnerPrinterOrderHistoryReceipt payload={data?.payload} />;
      default:
        return null;
    }
  }, [data]);

  return (
    <PrintContext.Provider value={context}>
      {renderReceipt}
      {children}
    </PrintContext.Provider>
  );
}
